import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Fingerprint, ShieldCheck } from 'lucide-react';

/**
 * Shared "slide to confirm" gesture used for both casting a vote (PollCard) and finishing
 * signup (EnrollmentModal). Previously each screen hand-built its own near-identical drag
 * math; this consolidates it into one component.
 *
 * Accessibility: the drag gesture is unchanged for mouse/touch, but the track is now a real
 * `role="slider"` that can receive keyboard focus. Arrow keys preview the fill; Enter/Space
 * completes the action immediately, exactly like reaching the end of the drag. Without this,
 * keyboard-only and switch-device users could not vote or finish onboarding at all.
 */
export default function SlideToConfirm({
    onConfirm,
    idleLabel = 'Slide to Confirm',
    draggingLabel = 'Confirming...',
    completeLabel = 'Confirmed!',
    ariaLabel,
    disabled = false,
    trackHeightClass = 'h-20',
    handleSize = 64,
    threshold = 0.98,
    completeIcon: CompleteIcon = ShieldCheck,
    idleIcon: IdleIcon = Fingerprint,
}) {
    const [dragX, setDragX] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [isComplete, setIsComplete] = useState(false);
    const sliderRef = useRef(null);
    const lastMilestone = useRef(0);
    const trackWidthRef = useRef(0);
    const rafRef = useRef(null);
    const dragOffsetRef = useRef(0);
    const trackPadding = 8;

    const complete = useCallback(() => {
        if (isComplete) return;
        setIsDragging(false);
        setDragX(1);
        setIsComplete(true);
        if (navigator.vibrate) navigator.vibrate([40, 30, 80]);
        onConfirm?.();
    }, [isComplete, onConfirm]);

    const handleDragStart = (e) => {
        if (disabled || isComplete) return;
        const rect = sliderRef.current.getBoundingClientRect();
        trackWidthRef.current = rect.width;
        const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
        const currentHandleX = dragX * (rect.width - trackPadding * 2 - handleSize);
        dragOffsetRef.current = clientX - (rect.left + trackPadding + currentHandleX + handleSize / 2);
        setIsDragging(true);
    };

    const handleDragMove = useCallback((e) => {
        if (!isDragging || !sliderRef.current || isComplete) return;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);

        rafRef.current = requestAnimationFrame(() => {
            const trackWidth = trackWidthRef.current;
            const clientX = e.clientX || (e.touches ? e.touches[0].clientX : 0);
            const rect = sliderRef.current.getBoundingClientRect();
            const availableSpace = trackWidth - trackPadding * 2 - handleSize;

            const rawX = clientX - rect.left - trackPadding - (handleSize / 2) - dragOffsetRef.current;
            const x = Math.max(0, Math.min(rawX, availableSpace));
            const progress = availableSpace > 0 ? x / availableSpace : 0;

            setDragX(progress);

            const currentMilestone = Math.floor(progress * 5) / 5;
            if (currentMilestone > lastMilestone.current && currentMilestone < 1) {
                if (navigator.vibrate) navigator.vibrate(5);
                lastMilestone.current = currentMilestone;
            }

            if (progress >= threshold) complete();
        });
    }, [isDragging, isComplete, threshold, complete]);

    const handleDragEnd = useCallback(() => {
        setIsDragging(false);
        lastMilestone.current = 0;
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setDragX(prev => (prev < threshold ? 0 : prev));
    }, [threshold]);

    useEffect(() => {
        if (isDragging) {
            window.addEventListener('pointermove', handleDragMove);
            window.addEventListener('pointerup', handleDragEnd);
            window.addEventListener('touchmove', handleDragMove, { passive: false });
            window.addEventListener('touchend', handleDragEnd);
        }
        return () => {
            window.removeEventListener('pointermove', handleDragMove);
            window.removeEventListener('pointerup', handleDragEnd);
            window.removeEventListener('touchmove', handleDragMove);
            window.removeEventListener('touchend', handleDragEnd);
        };
    }, [isDragging, handleDragMove, handleDragEnd]);

    const handleKeyDown = (e) => {
        if (disabled || isComplete) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            complete();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
            e.preventDefault();
            setDragX(prev => Math.min(1, prev + 0.1));
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
            e.preventDefault();
            setDragX(prev => Math.max(0, prev - 0.1));
        }
    };

    return (
        <div className="relative w-full max-w-xl mx-auto group/slider select-none">
            <div
                ref={sliderRef}
                role="slider"
                tabIndex={disabled ? -1 : 0}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round(dragX * 100)}
                aria-label={ariaLabel || idleLabel}
                aria-disabled={disabled}
                onKeyDown={handleKeyDown}
                className={`relative w-full ${trackHeightClass} rounded-full bg-gray-100/80 dark:bg-black/40 backdrop-blur-xl border border-gray-200 dark:border-white/10 transition-all duration-500 flex items-center p-2 overflow-hidden touch-none select-none focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-primary-600 focus-visible:outline-offset-2 ${disabled ? 'opacity-40 grayscale pointer-events-none' : 'opacity-100 shadow-inner cursor-pointer'}`}
                style={{ touchAction: 'none', userSelect: 'none' }}
            >
                <div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r from-violet-600 via-primary-600 to-primary-600/20 z-0 will-change-transform ${isDragging ? '' : 'transition-all duration-500'}`}
                    style={{ width: `calc(${dragX * 100}% + 40px)` }}
                />

                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10 pl-20 pr-10">
                    <AnimatePresence mode="wait">
                        {isComplete ? (
                            <motion.div key="complete" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex items-center gap-3 text-primary-600">
                                <ShieldCheck size={24} className="animate-bounce" />
                                <span className="text-lg font-black uppercase italic tracking-widest">{completeLabel}</span>
                            </motion.div>
                        ) : isDragging ? (
                            <motion.div key="dragging" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center">
                                <span className="text-2xs font-black text-white/60 uppercase tracking-[0.3em] italic animate-pulse">{draggingLabel}</span>
                                <span className="text-2xl font-black text-white italic transform skew-x-[-12deg] tracking-tight">{Math.floor(dragX * 100)}%</span>
                            </motion.div>
                        ) : (
                            <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className={`flex items-center gap-4 transition-all duration-500 ${dragX > 0.1 ? 'opacity-0 -translate-x-8 blur-sm' : 'opacity-100'}`}>
                                <span className="text-sm md:text-base font-black uppercase tracking-[0.3em] text-gray-900 dark:text-white italic animate-pulse">{idleLabel}</span>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <motion.div
                    aria-hidden="true"
                    style={{ x: dragX * (trackWidthRef.current ? (trackWidthRef.current - handleSize - 16) : 0), rotate: dragX * 360, userSelect: 'none' }}
                    onPointerDown={handleDragStart}
                    className={`rounded-full flex items-center justify-center shadow-2xl cursor-grab active:cursor-grabbing relative z-30 border-2 transition-all duration-200 select-none ${isDragging ? 'bg-primary-600 border-white' : 'bg-white dark:bg-zinc-900 border-gray-200 dark:border-white/10'}`}
                >
                    <div style={{ width: handleSize, height: handleSize }} className="flex items-center justify-center">
                        {isComplete ? (
                            <CompleteIcon size={28} className="text-white" />
                        ) : (
                            <IdleIcon size={28} className={`transition-all duration-300 ${isDragging ? 'text-white scale-110' : 'text-gray-400'}`} />
                        )}
                        {isDragging && (
                            <div className="absolute inset-0 bg-primary-600/40 blur-xl rounded-full animate-pulse" />
                        )}
                    </div>
                </motion.div>
            </div>

            <div className="h-6 flex items-center justify-center mt-3">
                {isDragging ? (
                    <p className="text-2xs font-black uppercase tracking-[0.4em] text-gray-500 dark:text-gray-400 italic">Human Validation in Progress</p>
                ) : !isComplete ? (
                    <p className="text-2xs font-black uppercase tracking-[0.4em] text-gray-500 dark:text-gray-500 italic opacity-70">
                        Tab to focus, then press Enter to confirm
                    </p>
                ) : null}
            </div>
        </div>
    );
}
